import React, { memo, useContext } from 'react';
import accounting from 'accounting-js';
import { AppContext } from '@prisjakt/ui-app-context';

import marketSettings from './market-settings';

import codes from './codes';

export const formatCurrency = (
  price: PriceType,
  market: string,
  asDiff: boolean,
  code: string,
  omitCode: boolean = false
) => {
  const settings = marketSettings[market];

  if (!settings || price === undefined || price === null) {
    return null;
  }

  const overrideCode = codes[code && code.toLowerCase()];
  const { withDecimals, numberOfDecimals, symbol, format } =
    overrideCode || settings;
  const decimals = numberOfDecimals !== undefined ? numberOfDecimals : 2;
  const options = {
    symbol: omitCode ? '¤' : symbol,
    decimal: settings.decimalSign,
    precision: withDecimals ? decimals : 0,
    thousand: settings.thousandSeparator,
    format,
  };

  let formattedPrice = accounting.formatMoney(price, options);

  if (omitCode) {
    formattedPrice = formattedPrice.replace('¤', '').trim();
  }

  if (asDiff && price > 0) {
    return `+${formattedPrice}`;
  }

  return formattedPrice;
};

const Currency: React.FC<React.PropsWithChildren<ICurrencyProps>> = props => {
  const { price, market, asDiff = false, code = '', omitCode = false } = props;
  const context = useContext(AppContext);
  const marketFromParamOrContext =
    market || (context.market && context.market.code);

  const curr = formatCurrency(
    price,
    marketFromParamOrContext,
    asDiff,
    code,
    omitCode
  );

  return <>{curr}</>;
};

interface ICurrencyProps {
  market?: string;
  price: PriceType;
  asDiff?: boolean;
  code?: string;
  omitCode?: boolean;
}
type PriceType = string | number | null | undefined;

const memoizedCurrency = memo(Currency);
memoizedCurrency.displayName = 'Currency';
export default memoizedCurrency;
